import Stats from 'frontend_stats';
import FullstoryUtil from 'fullstory_util';
import PersonalizeUtil from 'personalize_util';
import {
	getListingData,
	BrazeUtil,
	TrackUtil,
	getCustomData,
	plural,
	trackFbEvent,
	parseTrackingParams,
} from 'core/utils';
import { default as ErrorTracker } from 'error_tracker';
import { updateUserAttributes, getItemProps } from 'core/utils/Braze';

function setupJustunoCallback() {
	if (!window?.juapp || typeof window.juapp !== 'function') return;

	// Justuno Impression Callback
	// Reference: https://support.justuno.com/en/passing-information-from-justuno-popup-to-your-site
	window.juapp('trackFunc', 'impression', (email, eventname, promotitle, cmid) => {
		Stats.trackGeneric('justuno_impression', {
			cus1: email,
			cus2: eventname,
			cus3: promotitle,
			cus4: cmid,
		});
		FullstoryUtil.sendEvent('Justuno impression', {
			title_str: `${promotitle}`,
			cmid_str: `${cmid}`,
		});
	});
}

function setupOptimizeCallback() {
	if (!window?.gtag) {
		return;
	}

	window.gtag('event', 'optimize.callback', {
		callback: (combination, experimentId, containerId) => {
			// Nice-to-have debugging & tracking for Optimize AB experiences
			console.debug(`optimize.callback for http://optimize.google.com/experiences/${experimentId}?containerId=${containerId}&combination=${combination}`);

			Stats.trackGeneric('a/b test init', {
				cus1: 'experiment:' + experimentId,
				cus2: 'cell:' + combination,
			});

		}
	});
}

function fraudTracker() {
	// Intercept the original XMLHttpRequest object with a new object that adds an event listener for the "load" event
	const originalXHR = XMLHttpRequest;
	const targetDomain = "autofilluhrs.azurewebsites.net";
	// eslint-disable-next-line no-global-assign
	XMLHttpRequest = function() {
		const xhr = new originalXHR();
		xhr.addEventListener("load", function(event) {
			// Check if the XHR request was made to the target domain
			if (event.target.responseURL.includes(targetDomain)) {
				FullstoryUtil.sendEvent('Fraud Tracker', {
					trigger_str: 'XHR',
					data_str: `${event.target.responseURL}`,
				});
				Stats.trackGeneric('fraud_tracker', {
					cus1: 'XHR',
					cus2: `${event.target.responseURL}`,
				});
			}
		});
		return xhr;
	};

	// Track query param `UniqueID`
	const fraudParam = 'UniqueID';
	let searchParams = new URLSearchParams(window.location.search);
	if (searchParams.has(fraudParam)) {
		FullstoryUtil.sendEvent('Fraud Tracker', {
			trigger_str: 'UniqueID',
			data_str: `${searchParams.get(fraudParam)}`,
		});
		Stats.trackGeneric('fraud_tracker', {
			cus1: 'UniqueID',
			cus2: `${searchParams.get(fraudParam)}`,
		});
	}
	// Track referrer `prod.uhrs.playmsn.com`
	let r = document?.referrer || "";
	if (r.includes("prod.uhrs.playmsn.com")) {
		FullstoryUtil.sendEvent('Fraud Tracker', {
			trigger_str: 'referrer',
			data_str: `${r}`,
		});
		Stats.trackGeneric('fraud_tracker', {
			cus1: 'referrer',
			cus2: `${r}`,
		});
	}
	// Track clicks on `#ID_SmartSelectOverlay`
	const findParent = (el, selector, includeSelf = true) => {
		if (includeSelf && el.matches(selector)) {
			return el;
		}
		while ((el = el.parentNode) && el !== document) {
			if (!selector || el.matches(selector)) return el;
		}
		return null;
	};
	const fraudClickHandler = (e) => {
		const match = findParent(event.target, '#smart-context-menu');
		const match2 = findParent(event.target, '#ID_SmartSelectOverlay');
		if (!match && !match2) return;
		FullstoryUtil.sendEvent('Fraud Tracker', {
			trigger_str: 'click',
			data_str: match ? '#smart-context-menu' : '#ID_SmartSelectOverlay',
		});
		Stats.trackGeneric('fraud_tracker', {
			cus1: 'click',
			cus2: match ? '#smart-context-menu' : '#ID_SmartSelectOverlay',
		});
	};
	document.body.addEventListener('click', fraudClickHandler, false);
}



export default {
	base() {
		// Tracking
		BrazeUtil.initialize();
		Stats.registerGaParams();
		// Expose TrackUtil for backbone to consume
		window.TrackUtil = TrackUtil;
		Stats.trackImpression('marketplace');
		/* Shared Across Templates */
		Stats.trackLinks(".listing-box a");
		Stats.trackLinks(".exit-url");

		setupOptimizeCallback();
		setupJustunoCallback();

		Stats.trackPageload();
		if (window?.cj?.user) {
			const { email, id, name } = window.cj.user;
			ErrorTracker.setUser({
				email: email,
				id: id,
			});

			FullstoryUtil.identify(
				id,
				name,
				email,
			);
		}
		FullstoryUtil.sendUrlToSentry();

		fraudTracker();
	},

	trackQueryParams() {
		/*
		* Tracking query
		* `discovery`: event used to track buyers 'discovering' content (listings, collections, categories).
		* 		Used to get better insights on the effectiveness of merchandicing.
		* cus2: <pt> page_type:    assuming discovery url structure:
		* cus3: <gs> general_slug: `/<page_type>/<general_slug>`
		* cus4: <pn> page_number:  page within result set
		* cus5: <cn> card_number:  position within discovery page
		* cus6: <ft> filter_tags:  filter-group-tags applied to result set
		* cus7: <sn> section_name: section name card is displayed within
		*/
		const qParams = parseTrackingParams();
		if (qParams.pt || qParams.gs || qParams.cn || qParams.pn || qParams.ft || qParams.sn) {
			Stats.trackGeneric('discovery', {
				cus1: 'track',
				cus2: qParams.pt,
				cus3: qParams.gs,
				cus4: qParams.pn,
				cus5: qParams.cn,
				cus6: qParams.ft,
				cus7: qParams.sn,
			});
		}
	},

	listing() {
		const listing = getListingData();
		if (listing) {
			TrackUtil.trackEvent("product_view_exception", { braze: [] });
			TrackUtil.trackEvent("product_view", {
				braze: [getItemProps()],
			});

			// Handle personalize events & possible recommendation stats:
			PersonalizeUtil.onListingView(listing.listingId, listing.categories);

			trackFbEvent('ViewContent', {
				external_id: window.cj?.user?.id,
				content_type: 'product',
				content_ids: [listing.listingId],
			});

			updateUserAttributes({ boxesViewed: [listing.name] });
		}

		// Track the "in-house utm" params
		this.trackQueryParams();
	},

	checkout() {
		BrazeUtil.initialize();
		// Expose TrackUtil for backbone to consume
		window.TrackUtil = TrackUtil;
		window.BrazeUtil = BrazeUtil;
		// Checkout doesn't do `Analytics.base()`
		setupOptimizeCallback();
		fraudTracker();
	},

	discovery() {
		this.trackQueryParams();
		if (! window) return;
		const pageType = window?.cj?.page_type;
		const trackable = pageType === "category" || pageType === "subcategory" || pageType === "collection";
		if (! trackable) return;
		const headerProps = getCustomData(window.cj.header_props, {});
		const slug = headerProps.bottomNavProps.currentSlug;
		const attributes = {};
		const key = `${plural(pageType)}Viewed`;
		attributes[key] = [slug];
		updateUserAttributes(attributes);
	}
};
